/**
 * Section Nav Panel — left-hand rail for the Edit Resume lightbox.
 *
 * Lists every editor block as a nav row, grouped into six POSITIONAL bands
 * (Identity / Story / Evidence / Finish / Voice / Chrome Ext). Pinned rows
 * (Name, Contact Info, Cover Letter, Resume URLs, Define Synonyms, Writing
 * Sample, Implied Facts, Demographics) are locked in place; the nine real
 * resume sections are draggable and share the SAME `sectionOrder` state the
 * editor body and the Save payload already use — so a drag reorders the editor
 * scroll live and persists on Save.
 *
 * Band membership is derived from POSITION, never stored per-section: the bands
 * are six contiguous chunks of the flat section order, sized by `BandCounts`.
 * Dropping a section into another band's rows just moves it in the flat order
 * and shifts one count. Counts are UI-only (the backend persists a flat order),
 * so they live in localStorage keyed by resume name.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, GripVertical, Lock,
  AlertTriangle, Sparkles, ArrowRight,
} from 'lucide-react';
import { clampScore, overallScoreBarPct, overallScoreLabel } from '../utils/resumeLint';
import type { GenerationType, SubcomponentType } from '../types';

/**
 * Help/Docs anchor root. The rail only exists inside an OPEN resume editor, so
 * every key hangs off the editor's `mainapp.resumes.resumeN` path — see
 * `help/highlightTargets.ts`, which registers the matching catalog entries.
 * The collapsed icon rail reuses the SAME keys (only one variant renders at a
 * time), so a help step can point at a row whichever way the rail is set.
 */
const HELP = 'mainapp.resumes.resumeN.nav';

export type BandId = 'identity' | 'story' | 'evidence' | 'finish' | 'voice' | 'chromeext';
export type BandCounts = Record<BandId, number>;
export type SectionState = 'ready' | 'empty' | 'omitted';

export const BAND_ORDER: BandId[] = ['identity', 'story', 'evidence', 'finish', 'voice', 'chromeext'];

const BAND_LABELS: Record<BandId, string> = {
  identity: 'Identity',
  story: 'Story',
  evidence: 'Evidence',
  finish: 'Finish',
  voice: 'Voice',
  chromeext: 'Chrome Ext',
};

// Short badge used by the collapsed icon rail.
const BAND_SHORT: Record<BandId, string> = {
  identity: 'ID', story: 'ST', evidence: 'EV', finish: 'FI', voice: 'VO', chromeext: 'CX',
};

/**
 * Per-band surface tokens. Mirrors the approved `20-section-rail` mockup:
 * each band is a rounded, faintly tinted container with a colored left stripe
 * and a colored uppercase caption; rows sit INSIDE on white/hover surfaces.
 */
const BAND_STYLE: Record<BandId, { block: string; stripe: string; caption: string; initial: string }> = {
  identity:  { block: 'bg-primary-50 border-[#dceffd]', stripe: 'bg-primary-400', caption: 'text-primary-700', initial: 'text-primary-700' },
  story:     { block: 'bg-primary-100 border-[#cfeafb]', stripe: 'bg-primary-600', caption: 'text-primary-700', initial: 'text-primary-700' },
  evidence:  { block: 'bg-green-50 border-[#dcf7e3]',   stripe: 'bg-green-300',   caption: 'text-green-700',   initial: 'text-green-700' },
  finish:    { block: 'bg-gray-100 border-gray-200',    stripe: 'bg-gray-400',    caption: 'text-gray-500',    initial: 'text-gray-500' },
  voice:     { block: 'bg-purple-50 border-[#f2e8fd]',  stripe: 'bg-purple-400',  caption: 'text-purple-700',  initial: 'text-purple-700' },
  // Chrome Ext is the one band whose rows never print on a resume — amber keeps
  // it visually apart from the five that do, inside the same tinted language.
  chromeext: { block: 'bg-amber-50 border-[#fbeed3]',   stripe: 'bg-amber-300',   caption: 'text-amber-700',   initial: 'text-amber-700' },
};

// Rows that are never reordered. They render at the top of their band (the
// cover letter is the one trailing pin, so it renders after that band's rows).
interface PinnedRow {
  key: string;
  label: string;
  band: BandId;
  component?: SubcomponentType;  // only when the row maps to a real section
  trailing?: boolean;            // render after the band's draggable rows
  /** Static chip for rows with no generation type (Demographics never prints). */
  staticChip?: string;
}

// `basics` is the historical key of the block that now holds ONLY the
// professional name — kept so the help-target catalog, the scrollspy and the
// saved accordion state all stay valid; only its label changed to "Name".
export const PINNED_ROWS: PinnedRow[] = [
  { key: 'basics', label: 'Name', band: 'identity' },
  { key: 'contact', label: 'Contact Info', band: 'identity', component: 'contact' },
  { key: 'coverletter', label: 'Cover Letter', band: 'finish', component: 'coverletter', trailing: true },
  { key: 'resumeurls', label: 'Resume URLs', band: 'finish', trailing: true },
  { key: 'synonyms', label: 'Define Synonyms', band: 'voice' },
  { key: 'writingsample', label: 'Writing Sample', band: 'voice' },
  { key: 'impliedfacts', label: 'Implied Facts', band: 'voice' },
  { key: 'demographics', label: 'Demographics', band: 'chromeext', staticChip: 'PRIVATE' },
];

// Sections that read as narrative rather than evidence. Used ONLY to pick the
// default Story/Evidence boundary for a resume that has no saved band layout;
// because bands are positional the boundary lands after the LAST of these in
// the current order (so the chunks stay contiguous with the print order).
const STORY_PREFERRED = new Set<SubcomponentType>(['summary', 'highlights', 'experience']);

const GEN_MICROLABELS: Record<GenerationType, string> = {
  manual: 'MANUAL',
  embedding: 'AI·MATCH',
  generate: 'AI·GEN',
  omit: 'OMIT',
};

// Mono chip surfaces, per the mockup: MANUAL gray, AI·* purple, OMIT red.
const GEN_MICRO_CLASS: Record<GenerationType, string> = {
  manual: 'text-gray-500 bg-white/75 border-gray-900/10 hover:bg-white',
  embedding: 'text-purple-700 bg-purple-50/90 border-purple-200 hover:bg-purple-100',
  generate: 'text-purple-700 bg-purple-50/90 border-purple-200 hover:bg-purple-100',
  omit: 'text-red-700 bg-red-50/90 border-red-200 hover:bg-red-100',
};

const CHIP_BASE = 'font-mono text-[9.5px] leading-none tracking-[0.03em] rounded-[4px] border px-1 py-[2.5px] flex-shrink-0 transition-colors';

export function defaultBandCounts(ordered: SubcomponentType[]): BandCounts {
  let lastStory = -1;
  ordered.forEach((s, i) => { if (STORY_PREFERRED.has(s)) lastStory = i; });
  const story = lastStory + 1;
  return {
    identity: 0, story, evidence: ordered.length - story,
    finish: 0, voice: 0, chromeext: 0,
  };
}

/**
 * Coerce anything read back from localStorage into a usable layout.
 *
 * This is ALSO the migration path off the old five-band layout (identity /
 * voice / story / evidence / finish, in that positional order). Such a value
 * has no `chromeext` key, so it fails the completeness check below and falls
 * back to the defaults. That is deliberate: `voice` moved from second to fifth
 * position, so any section a user had dropped into it would silently change
 * band anyway — and remapping it "correctly" would mean reordering the flat
 * list, i.e. changing their PRINT order. Discarding the counts touches only the
 * band chunking; the flat drag order lives on the resume record and is
 * untouched.
 */
export function sanitizeBandCounts(raw: unknown, ordered: SubcomponentType[]): BandCounts {
  if (raw && typeof raw === 'object') {
    const c = raw as Partial<Record<BandId, unknown>>;
    const counts = {} as BandCounts;
    let total = 0;
    let ok = true;
    for (const b of BAND_ORDER) {
      const v = c[b];
      if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) { ok = false; break; }
      counts[b] = Math.floor(v);
      total += counts[b];
    }
    if (ok && total === ordered.length) return counts;
  }
  return defaultBandCounts(ordered);
}

/** Index range [start, end) of each band within the flat section order. */
function bandRanges(counts: BandCounts): Record<BandId, [number, number]> {
  const ranges = {} as Record<BandId, [number, number]>;
  let cursor = 0;
  for (const b of BAND_ORDER) {
    ranges[b] = [cursor, cursor + counts[b]];
    cursor += counts[b];
  }
  return ranges;
}

function bandOfIndex(idx: number, counts: BandCounts): BandId {
  let cursor = 0;
  for (const b of BAND_ORDER) {
    cursor += counts[b];
    if (idx < cursor) return b;
  }
  return 'evidence';
}

/**
 * Move `component` so it sits at `idxInBand` of `band`. Returns the new flat
 * order plus the new band sizes. Pure — exported for tests.
 */
export function applyBandDrop(
  ordered: SubcomponentType[],
  counts: BandCounts,
  component: SubcomponentType,
  band: BandId,
  idxInBand: number,
): { order: SubcomponentType[]; counts: BandCounts } {
  const from = ordered.indexOf(component);
  if (from < 0) return { order: ordered, counts };
  const ranges = bandRanges(counts);
  const globalInsert = Math.min(ranges[band][0] + idxInBand, ordered.length);
  const sourceBand = bandOfIndex(from, counts);
  const next = ordered.filter(s => s !== component);
  const insertAt = globalInsert > from ? globalInsert - 1 : globalInsert;
  next.splice(Math.max(0, Math.min(insertAt, next.length)), 0, component);
  const nextCounts: BandCounts = { ...counts };
  nextCounts[sourceBand] -= 1;
  nextCounts[band] += 1;
  return { order: next, counts: nextCounts };
}

interface SectionNavPanelProps {
  /** Reorderable sections in print order (contact/coverletter excluded). */
  orderedSections: SubcomponentType[];
  /** Display label for a section (reflects custom names live). */
  getLabel: (component: SubcomponentType) => string;
  genTypes: Record<SubcomponentType, GenerationType>;
  /** Row status by nav key (a section component, or a pinned key like 'basics'). */
  getState: (key: string) => SectionState;
  /** Pending-recommendation count per nav key — drives the amber "needs attention" dot. */
  attentionCounts?: Record<string, number>;
  /**
   * Pending synonym-suggestion count per nav key (today only 'synonyms') —
   * a small purple "N suggested" chip on that row
   * (features/synonym_suggestions.md). Separate from the amber review dot.
   */
  suggestionCounts?: Record<string, number>;
  /** Total pending recommendations; drives the footer readout. */
  recommendationCount?: number;
  /** Per-section breakdown of the pending recommendations, for the readout. */
  recommendationSections?: { section: string; count: number }[];
  /** Scroll the editor to the AI review area (footer chip). */
  onShowRecommendations?: () => void;
  /** Replace the flat section order (same state the editor body renders from). */
  onReorder: (next: SubcomponentType[]) => void;
  /** Accessibility fallback for drag — reuses the section header chevrons. */
  onMove: (component: SubcomponentType, direction: 'up' | 'down') => void;
  onCycleGenType: (component: SubcomponentType) => void;
  /** Scrollspy: nav key of the block currently in view. */
  activeKey: string | null;
  onNavigate: (key: string) => void;
  progress: { done: number; total: number };
  /**
   * Headline number for the rail: the deterministic Resume Scorecard overall
   * (0-100), recomputed live by the editor as the resume is edited. This — not
   * "sections ready" — is the score the bar reports.
   */
  score: number;
  /**
   * The Accuracy (server lint) pass hasn't landed yet, so `score` is still the
   * three-dimension number and WILL move. Renders NO number at all (a neutral
   * placeholder + empty bar + "Calculating resume score…") rather than showing
   * a score that is about to change — a transiently-wrong number reads as a bug.
   */
  scoreComputing?: boolean;
  /** Open the Scorecard modal (headline click-through). */
  onShowScorecard?: () => void;
  /** Persistence scope for band layout + collapsed state. */
  storageKey: string;
}

export function SectionNavPanel({
  orderedSections, getLabel, genTypes, getState, onReorder, onMove,
  onCycleGenType, activeKey, onNavigate, progress, storageKey, score, scoreComputing,
  onShowScorecard, attentionCounts, recommendationCount = 0,
  recommendationSections = [], onShowRecommendations, suggestionCounts,
}: SectionNavPanelProps) {
  const bandsKey = `ss_edit_nav_bands_${storageKey}`;
  const collapsedKey = 'ss_edit_nav_collapsed';

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(collapsedKey) === '1'; } catch { return false; }
  });
  const [counts, setCounts] = useState<BandCounts>(() => defaultBandCounts(orderedSections));
  const hydratedFor = useRef<string | null>(null);

  // Hydrate the band layout once per resume, then keep it in localStorage.
  useEffect(() => {
    if (hydratedFor.current === bandsKey) return;
    hydratedFor.current = bandsKey;
    let raw: unknown = null;
    try {
      const stored = localStorage.getItem(bandsKey);
      raw = stored ? JSON.parse(stored) : null;
    } catch { raw = null; }
    setCounts(sanitizeBandCounts(raw, orderedSections));
  }, [bandsKey, orderedSections]);

  // Self-heal if the section list ever changes size under a stored layout.
  const safeCounts = useMemo(() => {
    const total = BAND_ORDER.reduce((a, b) => a + counts[b], 0);
    if (total === orderedSections.length) return counts;
    return defaultBandCounts(orderedSections);
  }, [counts, orderedSections]);

  const persistCounts = useCallback((next: BandCounts) => {
    setCounts(next);
    try { localStorage.setItem(bandsKey, JSON.stringify(next)); } catch { /* non-fatal */ }
  }, [bandsKey]);

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(collapsedKey, next ? '1' : '0'); } catch { /* non-fatal */ }
      return next;
    });
  };

  const ranges = useMemo(() => bandRanges(safeCounts), [safeCounts]);

  // ---- drag state ---------------------------------------------------------
  const [dragging, setDragging] = useState<SubcomponentType | null>(null);
  const [dropTarget, setDropTarget] = useState<{ band: BandId; idx: number } | null>(null);
  const dropTargetRef = useRef<{ band: BandId; idx: number } | null>(null);
  const markerRefs = useRef<Map<string, HTMLElement>>(new Map());
  const bandRefs = useRef<Map<BandId, HTMLElement>>(new Map());

  const setMarkerRef = (key: string) => (el: HTMLElement | null) => {
    if (el) markerRefs.current.set(key, el);
    else markerRefs.current.delete(key);
  };

  const setBandRef = (band: BandId) => (el: HTMLElement | null) => {
    if (el) bandRefs.current.set(band, el);
    else bandRefs.current.delete(band);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMovePointer = (e: PointerEvent) => {
      // Two bands meet at exactly the same y, so distance alone can't say which
      // block the user means. The tinted band the pointer is INSIDE wins; only
      // when the pointer is outside every block do we search all markers.
      let hoverBand: BandId | null = null;
      bandRefs.current.forEach((el, band) => {
        const r = el.getBoundingClientRect();
        if (e.clientY >= r.top && e.clientY <= r.bottom) hoverBand = band;
      });
      let best: { band: BandId; idx: number } | null = null;
      let bestDist = Infinity;
      markerRefs.current.forEach((el, key) => {
        const [bandKey, idx] = key.split(':');
        if (hoverBand && bandKey !== hoverBand) return;
        const r = el.getBoundingClientRect();
        const dist = Math.abs((r.top + r.bottom) / 2 - e.clientY);
        if (dist < bestDist) {
          bestDist = dist;
          best = { band: bandKey as BandId, idx: Number(idx) };
        }
      });
      dropTargetRef.current = best;
      setDropTarget(best);
    };
    const onUp = () => {
      const target = dropTargetRef.current;
      const component = dragging;
      setDragging(null);
      setDropTarget(null);
      dropTargetRef.current = null;
      if (!target || !component) return;
      const result = applyBandDrop(orderedSections, safeCounts, component, target.band, target.idx);
      persistCounts(result.counts);
      onReorder(result.order);
    };
    window.addEventListener('pointermove', onMovePointer);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMovePointer);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging, orderedSections, safeCounts, onReorder, persistCounts]);

  // ---- row model ----------------------------------------------------------
  const totalPrint = orderedSections.length + 2;  // contact + sections + cover letter
  const printPosition = (key: string, component?: SubcomponentType): number | null => {
    if (key === 'contact') return 1;
    if (key === 'coverletter') return totalPrint;
    if (!component) return null;
    const i = orderedSections.indexOf(component);
    return i < 0 ? null : i + 2;
  };

  // Headline number = the Resume Scorecard overall, clamped for display. While
  // the first Accuracy pass is still running the number isn't knowable, so the
  // readout is a neutral placeholder and the bar is empty — never the
  // lint-less (higher) number that is about to change.
  const scorePct = clampScore(score);
  const scoreLabel = overallScoreLabel(score, scoreComputing);
  const scoreBarPct = overallScoreBarPct(score, scoreComputing);

  // ---- status vocabulary --------------------------------------------------
  // green = ready, amber = has open recommendations, gray = empty,
  // hollow = omitted (matches the mockup's dot language).
  type DotState = 'ready' | 'attention' | 'empty' | 'omitted';

  const attentionOf = (key: string) => attentionCounts?.[key] ?? 0;

  function rowDot(key: string): DotState {
    const state = getState(key) || 'ready';
    if (state === 'omitted') return 'omitted';
    if (attentionOf(key) > 0) return 'attention';
    return state === 'empty' ? 'empty' : 'ready';
  }

  // Honest header summary: every row we actually render, counted once.
  const allRowKeys = [...PINNED_ROWS.map(r => r.key), ...orderedSections as string[]];
  const attentionRows = allRowKeys.filter(k => rowDot(k) === 'attention').length;
  const emptyRows = allRowKeys.filter(k => rowDot(k) === 'empty').length;

  function statusDot(key: string) {
    const d = rowDot(key);
    if (d === 'omitted') return <span className="w-2 h-2 rounded-full bg-white border-[1.5px] border-gray-300 flex-shrink-0" title="Omitted — not printed" />;
    if (d === 'attention') return <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" title={`${attentionOf(key)} recommendation(s) to review`} />;
    if (d === 'empty') return <span className="w-2 h-2 rounded-full bg-white border-[1.5px] border-gray-300 flex-shrink-0" title="Empty" />;
    return <span className="w-2 h-2 rounded-full bg-green-700 flex-shrink-0" title="Has content" />;
  }

  function genChip(component: SubcomponentType) {
    const t = genTypes[component];
    if (!t) return null;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onCycleGenType(component); }}
        data-help-target={`${HELP}.row.${component}.gentype`}
        className={`${CHIP_BASE} ${GEN_MICRO_CLASS[t]}`}
        title="Click to change how this section is generated"
      >
        {GEN_MICROLABELS[t]}
      </button>
    );
  }

  function positionPointer(rowKey: string, pos: number, hideOnHover: boolean) {
    return (
      <span
        data-help-target={`${HELP}.row.${rowKey}.position`}
        className={`text-[9px] text-gray-400 tabular-nums flex-shrink-0 w-[26px] text-right ${
          hideOnHover ? 'group-hover:opacity-0 transition-opacity' : ''
        }`}
        title={`Position ${pos} of ${totalPrint} in the generated resume`}
      >
        {pos}/{totalPrint}
      </span>
    );
  }

  function dropMarker(band: BandId, idx: number) {
    const active = dropTarget?.band === band && dropTarget.idx === idx;
    return (
      <div ref={setMarkerRef(`${band}:${idx}`)} className="relative h-0 z-20">
        {active && (
          <>
            <span className="absolute -top-px left-1 right-0.5 h-0.5 rounded bg-primary-600 shadow-[0_0_0_3px_rgba(2,132,199,0.18)]" />
            <span className="absolute -top-[3px] left-0 w-[7px] h-[7px] rounded-full bg-white border-2 border-primary-600" />
          </>
        )}
      </div>
    );
  }

  const ROW_BASE = 'group relative flex items-center gap-[7px] h-7 pl-[3px] pr-[7px] rounded-[7px] border transition-colors';
  const rowSurface = (isActive: boolean) => isActive
    ? 'bg-white border-primary-500 shadow-[0_1px_3px_rgba(17,24,39,0.09)]'
    : 'border-transparent hover:bg-white/90 hover:border-gray-900/10';
  const nameClass = (isActive: boolean) =>
    `flex-1 min-w-0 text-left truncate text-[13px] ${isActive ? 'text-primary-700 font-semibold' : 'text-gray-700'}`;

  function pinnedRow(row: PinnedRow) {
    const pos = printPosition(row.key, row.component);
    const label = row.component ? getLabel(row.component) : row.label;
    const isActive = activeKey === row.key;
    return (
      <div key={row.key} data-nav-row={row.key} data-help-target={`${HELP}.row.${row.key}`} className={`${ROW_BASE} ${rowSurface(isActive)}`}>
        <span
          className="w-[15px] h-[22px] grid place-items-center flex-shrink-0 text-gray-400 opacity-[0.55]"
          title="Pinned — this row keeps its position"
        >
          <Lock className="w-3 h-3" aria-hidden="true" />
        </span>
        {statusDot(row.key)}
        <button
          type="button"
          onClick={() => onNavigate(row.key)}
          className={nameClass(isActive)}
          title={`${label} — fixed position`}
        >
          {label}
        </button>
        {(suggestionCounts?.[row.key] ?? 0) > 0 && (
          <span
            data-testid={`nav-suggestion-chip-${row.key}`}
            data-help-target={`${HELP}.row.${row.key}.suggested`}
            className={`${CHIP_BASE} text-purple-700 bg-purple-50 border-purple-200`}
            title={`${suggestionCounts![row.key]} synonym suggestion(s) to review`}
          >
            {suggestionCounts![row.key]} suggested
          </span>
        )}
        {row.component
          ? genChip(row.component)
          : row.staticChip && (
            <span data-help-target={`${HELP}.row.${row.key}.chip`} className={`${CHIP_BASE} text-gray-500 bg-white/75 border-gray-900/10`} title="Never printed on the resume — used for application questions only">
              {row.staticChip}
            </span>
          )}
        {pos !== null ? positionPointer(row.key, pos, false) : <span className="w-[26px] flex-shrink-0" />}
      </div>
    );
  }

  function sectionRow(component: SubcomponentType) {
    const flatIdx = orderedSections.indexOf(component);
    const isActive = activeKey === component;
    const isDragging = dragging === component;
    const pos = printPosition(component, component);
    return (
      <div
        key={component}
        data-nav-row={component}
        data-help-target={`${HELP}.row.${component}`}
        className={`${ROW_BASE} ${
          isDragging
            ? 'bg-white border-primary-500 shadow-[0_10px_22px_rgba(17,24,39,0.18)] rotate-[-0.6deg] scale-[1.015] z-10'
            : rowSurface(isActive)
        }`}
      >
        <span
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            dropTargetRef.current = null;
            setDragging(component);
          }}
          data-help-target={`${HELP}.row.${component}.drag`}
          className={`w-[15px] h-[22px] grid place-items-center rounded flex-shrink-0 touch-none cursor-grab active:cursor-grabbing transition-opacity ${
            isDragging ? 'opacity-100 text-primary-600' : 'opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700 hover:bg-gray-900/5'
          }`}
          title={`Drag to reorder ${getLabel(component)}`}
          aria-hidden="true"
        >
          <GripVertical className="w-[13px] h-[13px]" />
        </span>
        {statusDot(component)}
        <button
          type="button"
          onClick={() => onNavigate(component)}
          className={nameClass(isActive)}
        >
          {getLabel(component)}
        </button>
        {genChip(component)}
        {pos !== null && positionPointer(component, pos, true)}
        {/* Keyboard/click fallback for drag — overlays the position pointer on hover. */}
        <span className="absolute right-[5px] flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => onMove(component, 'up')}
            disabled={flatIdx === 0}
            aria-label={`Move ${getLabel(component)} up`}
            className="text-gray-400 hover:text-gray-700 disabled:opacity-20"
          >
            <ChevronUp className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => onMove(component, 'down')}
            disabled={flatIdx === orderedSections.length - 1}
            aria-label={`Move ${getLabel(component)} down`}
            className="text-gray-400 hover:text-gray-700 disabled:opacity-20"
          >
            <ChevronDown className="w-3 h-3" />
          </button>
        </span>
      </div>
    );
  }

  // ---- collapsed icon rail ------------------------------------------------
  if (collapsed) {
    return (
      <div className="hidden md:flex flex-col w-[56px] border-r border-gray-200 bg-gray-50 flex-shrink-0" data-help-target={HELP} data-testid="section-nav-rail">
        <div className="flex items-center justify-center p-1.5 border-b border-gray-200" data-help-target={`${HELP}.header`}>
          <button
            type="button"
            onClick={toggleCollapsed}
            data-help-target={`${HELP}.collapse`}
            className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded"
            title="Expand section list"
            aria-label="Expand section list"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div
          data-help-target={`${HELP}.score`}
          data-testid="nav-score-collapsed"
          data-computing={scoreComputing ? '1' : '0'}
          className={`px-1.5 py-2 text-center text-[11px] font-bold text-primary-700 tabular-nums${scoreComputing ? ' animate-pulse opacity-60' : ''}`}
          title={scoreComputing
            ? 'Calculating your resume score…'
            : `Resume score ${scorePct} · ${progress.done} of ${progress.total} sections ready`}
        >
          {scoreLabel}
        </div>
        <div className="flex-1 overflow-y-auto pb-3 px-1">
          {BAND_ORDER.map(band => {
            const pins = PINNED_ROWS.filter(r => r.band === band);
            const secs = orderedSections.slice(ranges[band][0], ranges[band][1]);
            const style = BAND_STYLE[band];
            return (
              <div key={band} data-help-target={`${HELP}.band.${band}`} className={`mb-1.5 rounded-[9px] border py-1 ${style.block}`}>
                <div className={`text-[9px] font-bold tracking-[0.1em] text-center py-0.5 ${style.caption}`}>{BAND_SHORT[band]}</div>
                <div className="flex flex-col items-center gap-1">
                  {pins.filter(p => !p.trailing).map(p => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => onNavigate(p.key)}
                      title={p.component ? getLabel(p.component) : p.label}
                      data-help-target={`${HELP}.row.${p.key}`}
                      className={`w-7 h-7 rounded-full text-[10px] font-semibold flex items-center justify-center ${
                        activeKey === p.key ? 'bg-primary-100 text-primary-800 ring-1 ring-primary-300' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {(p.component ? getLabel(p.component) : p.label).slice(0, 2)}
                    </button>
                  ))}
                  {secs.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => onNavigate(s)}
                      title={`${getLabel(s)} — ${printPosition(s, s)} of ${totalPrint}`}
                      data-help-target={`${HELP}.row.${s}`}
                      className={`w-7 h-7 rounded-full text-[10px] font-semibold flex items-center justify-center ${
                        activeKey === s ? 'bg-primary-100 text-primary-800 ring-1 ring-primary-300' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {getLabel(s).slice(0, 2)}
                    </button>
                  ))}
                  {pins.filter(p => p.trailing).map(p => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => onNavigate(p.key)}
                      title={p.component ? getLabel(p.component) : p.label}
                      data-help-target={`${HELP}.row.${p.key}`}
                      className={`w-7 h-7 rounded-full text-[10px] font-semibold flex items-center justify-center ${
                        activeKey === p.key ? 'bg-primary-100 text-primary-800 ring-1 ring-primary-300' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {(p.component ? getLabel(p.component) : p.label).slice(0, 2)}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- expanded panel -----------------------------------------------------
  return (
    <div className="hidden md:flex flex-col w-[292px] border-r border-gray-200 bg-gray-50 flex-shrink-0" data-help-target={HELP} data-testid="section-nav-panel">
      {/* Status summary — thick score BAR (owner's choice over the mockup's
          ring), reporting the deterministic Resume Scorecard overall. */}
      <div className="px-3.5 pt-3 pb-2.5 border-b border-gray-200" data-help-target={`${HELP}.header`}>
        <div className="flex items-baseline justify-between gap-2">
          <button
            type="button"
            onClick={onShowScorecard}
            disabled={!onShowScorecard}
            data-help-target={`${HELP}.score`}
            data-testid="nav-score-headline"
            data-computing={scoreComputing ? '1' : '0'}
            className={`text-[19px] font-bold tracking-tight text-gray-900 tabular-nums leading-none text-left disabled:cursor-default enabled:hover:text-primary-700 transition-colors${scoreComputing ? ' animate-pulse opacity-60' : ''}`}
            title={scoreComputing
              ? 'Calculating your resume score (checking spelling and grammar)…'
              : 'Resume Scorecard overall — click for the full scorecard'}
          >
            {scoreLabel}
            <span className="ml-1.5 text-[10px] font-medium tracking-normal text-gray-500">· Scorecard</span>
          </button>
          <button
            type="button"
            onClick={toggleCollapsed}
            data-help-target={`${HELP}.collapse`}
            className="-mr-1 p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded self-start"
            title="Collapse section list"
            aria-label="Collapse section list"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>
        <div
          className="mt-2 h-2.5 w-full bg-gray-200 rounded-full overflow-hidden"
          data-help-target={`${HELP}.scorebar`}
          role="progressbar"
          // Indeterminate (no aria-valuenow) while the score is still being
          // computed — an assistive reader must not hear the lint-less number
          // any more than a sighted user sees it.
          aria-valuenow={scoreComputing ? undefined : scorePct}
          aria-valuetext={scoreComputing ? 'Calculating resume score' : undefined}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Resume score"
        >
          <div className="h-full bg-primary-600 rounded-full transition-all" style={{ width: `${scoreBarPct}%` }} />
        </div>
        <div className={`mt-2 text-[12px] font-semibold text-gray-900 leading-[1.35]${scoreComputing ? ' animate-pulse opacity-60' : ''}`}>
          {scoreComputing ? 'Calculating resume score…' : `${scorePct}% Resume score`}
        </div>
        <div className="mt-px text-[11px] text-gray-500">
          {progress.done} of {progress.total} sections ready
        </div>
        {attentionRows > 0 && (
          <div className="mt-[3px] flex items-center gap-1.5 text-[11px] text-amber-700">
            <AlertTriangle className="w-[11px] h-[11px] flex-shrink-0" aria-hidden="true" />
            {attentionRows} need{attentionRows === 1 ? 's' : ''} attention
          </div>
        )}
        {emptyRows > 0 && (
          <div className="mt-px text-[11px] text-gray-500">
            {emptyRows} section{emptyRows === 1 ? ' is' : 's are'} still empty
          </div>
        )}
      </div>

      {/* List caption */}
      <div className="px-3.5 pt-2.5 pb-1.5">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-gray-500">Resume order</span>
          <span className="text-[10.5px] text-gray-400 tabular-nums">{totalPrint} sections</span>
        </div>
        <p className="mt-0.5 text-[10.5px] leading-[1.4] text-gray-500">
          Drag to reorder - resumes follow this order.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-2.5 pb-2">
        {BAND_ORDER.map(band => {
          const pins = PINNED_ROWS.filter(r => r.band === band);
          const secs = orderedSections.slice(ranges[band][0], ranges[band][1]);
          const style = BAND_STYLE[band];
          const rowCount = pins.length + secs.length;
          return (
            <div
              key={band}
              ref={setBandRef(band)}
              data-nav-band={band}
              data-help-target={`${HELP}.band.${band}`}
              className={`relative rounded-[9px] border px-[3px] pt-0.5 pb-[3px] mb-0.5 ${style.block}`}
            >
              <span className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[9px] ${style.stripe}`} aria-hidden="true" />
              <div className={`flex items-center gap-1.5 pl-2 pr-1.5 pt-[3px] pb-0.5 text-[9.5px] font-bold uppercase tracking-[0.1em] ${style.caption}`}>
                <span>{BAND_LABELS[band]}</span>
                <span className="ml-auto text-[10.5px] font-semibold normal-case tracking-normal opacity-75">
                  {rowCount} section{rowCount === 1 ? '' : 's'}
                </span>
              </div>
              {pins.filter(p => !p.trailing).map(pinnedRow)}
              {dropMarker(band, 0)}
              {secs.map((s, i) => (
                <div key={s}>
                  {sectionRow(s)}
                  {dropMarker(band, i + 1)}
                </div>
              ))}
              {pins.filter(p => p.trailing).map(pinnedRow)}
              {rowCount === 0 && (
                <div className="px-2 py-1.5 text-[11px] text-gray-400 italic">drop a section here</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="border-t border-gray-200">
        {/* Recommendations readout — the rail's summary of the AI review.
            The individual recommendation cards stay inline in their sections. */}
        <div className="px-2 pt-2" data-help-target={`${HELP}.recommendations`} data-testid="nav-recommendations">
          {recommendationCount > 0 && onShowRecommendations ? (
            <button
              type="button"
              onClick={onShowRecommendations}
              className="flex items-center gap-2 w-full px-2.5 py-[7px] rounded-lg bg-purple-50 border border-purple-200 hover:bg-purple-100 text-left transition-colors"
              title="Jump to the AI review notes"
            >
              <Sparkles className="w-4 h-4 text-purple-700 flex-shrink-0" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-[12.5px] font-semibold text-purple-700 truncate">
                  {recommendationCount} recommendation{recommendationCount === 1 ? '' : 's'}
                </span>
                <span className="block text-[11px] text-purple-600 truncate">
                  {recommendationSections.length > 0
                    ? recommendationSections.map(s => `${s.section} ${s.count}`).join(' · ')
                    : 'Jump to the review notes'}
                </span>
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-purple-700 ml-auto flex-shrink-0" aria-hidden="true" />
            </button>
          ) : (
            <div className="flex items-center gap-2 w-full px-2.5 py-[7px] rounded-lg bg-gray-100 border border-gray-200">
              <Sparkles className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-[12.5px] font-semibold text-gray-600 truncate">No open recommendations</span>
                <span className="block text-[11px] text-gray-500 truncate">Run &ldquo;Review resume&rdquo; for AI notes</span>
              </span>
            </div>
          )}
        </div>
        <div className="px-3.5 py-2.5 text-[11px] leading-snug text-gray-500" data-help-target={`${HELP}.note`}>
          <span className="font-semibold text-gray-600">Nothing here is the final resume.</span>{' '}
          SkillSnap matches your most relevant experience and generates a fresh resume per job.
        </div>
      </div>
    </div>
  );
}
